import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Crown, 
  ChevronRight,
  BarChart3,
  Users,
  Target,
  Lock,
  Settings,
  TrendingUp,
  Lightbulb,
  HandshakeIcon,
  Brain,
  Compass
} from 'lucide-react';

const LeadershipTraining = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  // Leadership Training modules (all coming soon for now)
  const leadershipModules = [
    {
      id: 'strategic-thinking',
      title: 'Strategic Thinking',
      description: 'Develop skills to analyze complex situations, anticipate challenges, and create long-term plans that drive organizational success.',
      icon: Compass,
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30',
      borderColor: 'border-indigo-200 dark:border-indigo-800',
      features: ['Vision Development', 'Decision Frameworks', 'Risk Assessment', 'Goal Setting'],
      enabled: false,
      comingSoon: true
    },
    {
      id: 'team-leadership',
      title: 'Team Leadership',
      description: 'Master the art of building, motivating, and leading high-performing teams. Learn delegation, feedback, and conflict resolution.',
      icon: Users,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30',
      borderColor: 'border-blue-200 dark:border-blue-800',
      features: ['Team Building', 'Delegation', 'Conflict Resolution', 'Performance Management'],
      enabled: false,
      comingSoon: true
    },
    {
      id: 'emotional-intelligence',
      title: 'Emotional Intelligence',
      description: 'Enhance your ability to understand and manage emotions - both your own and others - to build stronger workplace relationships.',
      icon: Brain,
      color: 'from-rose-500 to-pink-600',
      bgColor: 'bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30',
      borderColor: 'border-rose-200 dark:border-rose-800',
      features: ['Self-Awareness', 'Empathy', 'Social Skills', 'Self-Regulation'],
      enabled: false,
      comingSoon: true
    },
    {
      id: 'coaching-mentoring',
      title: 'Coaching & Mentoring',
      description: 'Learn effective coaching techniques to develop talent, provide constructive feedback, and help team members reach their potential.',
      icon: HandshakeIcon,
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30',
      borderColor: 'border-emerald-200 dark:border-emerald-800',
      features: ['Active Listening', 'Feedback Techniques', 'Goal Alignment', 'Career Development'],
      enabled: false,
      comingSoon: true
    },
    {
      id: 'change-management',
      title: 'Change Management',
      description: 'Guide your organization through transitions with confidence. Learn to communicate change, manage resistance, and drive adoption.',
      icon: TrendingUp,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30',
      borderColor: 'border-amber-200 dark:border-amber-800',
      features: ['Change Communication', 'Stakeholder Buy-in', 'Resistance Management', 'Transition Planning'],
      enabled: false,
      comingSoon: true
    },
    {
      id: 'innovation-creativity',
      title: 'Innovation & Creativity',
      description: 'Foster a culture of innovation. Learn techniques to generate ideas, encourage experimentation, and drive continuous improvement.',
      icon: Lightbulb,
      color: 'from-yellow-500 to-amber-600',
      bgColor: 'bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-950/30 dark:to-amber-950/30',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      features: ['Creative Problem Solving', 'Design Thinking', 'Brainstorming', 'Innovation Culture'],
      enabled: false,
      comingSoon: true
    }
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-12 w-64" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-64" />)}
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-8" data-testid="leadership-training-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Leadership Training
            </h1>
            <p className="text-muted-foreground mt-1">
              {isAdmin 
                ? 'Manage leadership development programs'
                : 'Develop essential leadership skills for career growth'
              }
            </p>
          </div>
          {isAdmin && (
            <Button variant="outline" onClick={() => navigate('/settings')}>
              <Settings className="h-4 w-4 mr-2" />
              Module Settings
            </Button>
          )}
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 border-indigo-200 dark:border-indigo-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-indigo-600" />
                <div>
                  <p className="text-xl font-bold">6</p>
                  <p className="text-xs text-muted-foreground">Modules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-xl font-bold">-</p>
                  <p className="text-xs text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 border-emerald-200 dark:border-emerald-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-xl font-bold">-</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border-amber-200 dark:border-amber-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-xl font-bold">-</p>
                  <p className="text-xs text-muted-foreground">Avg Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leadership Modules Grid */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Leadership Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {leadershipModules.map((module) => {
              const Icon = module.icon;
              const isDisabled = !module.enabled;
              return (
                <Card 
                  key={module.id}
                  className={`relative overflow-hidden transition-all ${
                    isDisabled 
                      ? 'opacity-70 cursor-not-allowed' 
                      : 'hover:shadow-lg hover:-translate-y-1 cursor-pointer'
                  } group ${module.bgColor} ${module.borderColor}`}
                  data-testid={`leadership-module-${module.id}`}
                >
                  {/* Coming Soon Overlay */}
                  {module.comingSoon && (
                    <div className="absolute inset-0 bg-white/50 dark:bg-black/30 z-10 flex items-center justify-center">
                      <Badge className="bg-slate-600 text-white text-sm px-4 py-1.5">
                        <Lock className="h-4 w-4 mr-2" />
                        Coming Soon
                      </Badge>
                    </div>
                  )}
                  
                  {/* Gradient Banner */}
                  <div className={`h-1.5 bg-gradient-to-r ${module.color}`} />
                  
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${module.color} text-white shadow-lg ${!isDisabled && 'group-hover:scale-110'} transition-transform`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                    <CardTitle className={`text-lg mt-3 ${!isDisabled && 'group-hover:text-primary'} transition-colors`}>
                      {module.title}
                    </CardTitle>
                    <CardDescription className="text-sm line-clamp-2">
                      {module.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    {/* Features */}
                    <div className="flex flex-wrap gap-1.5">
                      {module.features.slice(0, 3).map((feature, i) => (
                        <Badge key={i} variant="outline" className="text-xs font-normal bg-white/50 dark:bg-black/20">
                          {feature}
                        </Badge>
                      ))}
                      {module.features.length > 3 && (
                        <Badge variant="outline" className="text-xs font-normal bg-white/50 dark:bg-black/20">
                          +{module.features.length - 3}
                        </Badge>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex items-center justify-between pt-2 border-t border-border/50">
                      <span className={`text-sm font-medium ${isDisabled ? 'text-muted-foreground' : 'text-primary'}`}>
                        {isDisabled ? 'Coming Soon' : 'Start Training'}
                      </span>
                      {!isDisabled && (
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
          <p className="text-sm text-indigo-800 dark:text-indigo-200">
            <strong>Leadership Training</strong> modules are designed to develop essential management and leadership competencies. 
            These programs will help you become a more effective leader, whether you&apos;re managing a team, driving change, 
            or aspiring to executive roles. <strong>Coming soon!</strong>
          </p>
        </div>

        {/* Admin Section */}
        {isAdmin && (
          <Card className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-950/50 dark:to-gray-950/50 border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Administration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Leadership Training modules are in development. Configure settings and content when modules become available.
              </p>
              <Button 
                variant="outline" 
                className="justify-start h-auto py-3"
                onClick={() => navigate('/settings')}
              >
                <Settings className="h-5 w-5 mr-3" />
                <div className="text-left">
                  <p className="font-medium">Platform Settings</p>
                  <p className="text-xs text-muted-foreground">Configure training platform</p>
                </div>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default LeadershipTraining;
